import React, { ReactElement } from 'react'
import {
  HeadingText,
  Container,
  Row,
  Col,
  BodyText,
  Link,
  CaptionText,
} from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import * as styles from './intro.css'
import { getImageSrcSet } from './IntroHelpers'
import { PAYPAL_OBJECTS_DIRECTORY } from '../../constants'
import { ImageConfiguration } from '../../../../typings'
import {
  isUserSpecificEaseOfUseIntroEnabled,
  isUserSpecificTransparencyEnabled,
  isUserTypeAgnosticIntroEnabled,
} from '../../utils/features'

type IntroPage = {
  id: string
  images: ImageConfiguration
  title: string
  firstIcon: ReactElement
  firstSubtitle: string
  secondIcon: ReactElement
  secondSubtitle: string
  showPrivacyLink?: boolean
}
type Props = {
  item: IntroPage
  goToPrivacyScreen: () => void
  isSmallerScreen: boolean
  children?: React.ReactNode
}
export function ContainerGeneration(props: Props) {
  const { item, goToPrivacyScreen, isSmallerScreen, children } = props
  const BulletText = isSmallerScreen ? CaptionText : BodyText
  const isExperimentIntroUser =
    isUserTypeAgnosticIntroEnabled() ||
    isUserSpecificEaseOfUseIntroEnabled() ||
    isUserSpecificTransparencyEnabled()
  const introIcons = isExperimentIntroUser
    ? styles.experimentIntroUserIcons
    : styles.introIcons

  const iconStyle = isExperimentIntroUser && styles.iconColumnStyle
  const textStyle = isExperimentIntroUser && styles.textColumnStyle

  return (
    <Container fluid className={styles.container} form={true}>
      <Row row={{ alignContent: 'center' }} noGutters={true}>
        <Col className={styles.mainImageContainer}>
          <picture>
            <img
              className={styles.carouselImage}
              srcSet={getImageSrcSet(PAYPAL_OBJECTS_DIRECTORY, item.images)}
              alt=""
              aria-hidden="true"
            />
          </picture>
        </Col>
      </Row>
      <Row noGutters={true}>
        <Col>
          <HeadingText
            as="h1"
            size="sm"
            type="strong"
            className={styles.heading}
          >
            <Message id={item.title} />
          </HeadingText>
        </Col>
      </Row>
      <Row noGutters={true}>
        {isExperimentIntroUser ? (
          <Col>
            <div className={styles.row}>
              <div className={iconStyle}>
                <div className={introIcons}>{item.firstIcon}</div>
              </div>
              <div className={textStyle}>
                <BulletText as="p" className={styles.introText}>
                  <Message id={item.firstSubtitle} />
                </BulletText>
              </div>
            </div>
          </Col>
        ) : (
          <>
            <Col xs={2}>
              <div className={introIcons}>{item.firstIcon}</div>
            </Col>
            <Col xs={10}>
              <BulletText as="p" className={styles.introText}>
                <Message id={item.firstSubtitle} />
              </BulletText>
            </Col>
          </>
        )}
      </Row>
      <Row noGutters={true}>
        {isExperimentIntroUser ? (
          <Col>
            <div className={styles.row}>
              <div className={iconStyle}>
                <div className={introIcons}>{item.secondIcon}</div>
              </div>
              <div className={textStyle}>
                <BulletText as="p" className={styles.introText}>
                  <Message id={item.secondSubtitle} />
                  {item.showPrivacyLink && (
                    <>
                      <br />
                      <Link
                        data-testid="learnMoreButton"
                        onClick={goToPrivacyScreen}
                        tabIndex="1"
                      >
                        <Message id="create.intro.learnMoreButton" />
                      </Link>
                    </>
                  )}
                </BulletText>
              </div>
            </div>
          </Col>
        ) : (
          <>
            <Col xs={2}>
              <div className={introIcons}>{item.secondIcon}</div>
            </Col>
            <Col xs={10}>
              <BulletText as="p" className={styles.introText}>
                <Message id={item.secondSubtitle} />
                {item.showPrivacyLink && (
                  <>
                    {' '}
                    <Link
                      data-testid="learnMoreButton"
                      onClick={goToPrivacyScreen}
                      tabIndex="1"
                    >
                      <Message id="create.intro.learnMoreButton" />
                    </Link>
                  </>
                )}
              </BulletText>
            </Col>
          </>
        )}
      </Row>
      {children}
    </Container>
  )
}
